import {
  Callout,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatDate,
  useLoad,
  SortTh,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../../client.ts';
import { t } from '../../../messages.ts';
import { Failure } from '../../shared.tsx';

/**
 * §3.9d — what support has proposed to this operator, and what came of it.
 *
 * Kept beside the operator rather than inside each brand's popup: the question
 * "is anything of ours waiting on them" is asked about the operator, and a
 * queue split across however many brand popups is one nobody can answer it
 * from. Loaded on demand like the other operator panels — every read here is a
 * logged cross-tenant access (§3.9c), not a free refresh.
 */
export function OperatorChangeRequestsPanel({ api, operatorId, shown, onShow, version }: {
  api: PlatformApi;
  operatorId: string;
  shown: boolean;
  onShow: () => void;
  version: number;
}) {
  const loaded = useLoad(
    () => (shown ? api.changeRequests(operatorId) : Promise.resolve(null)),
    [operatorId, shown, version],
  );

  return (
    <Panel
      title={t('ops.changes.title')}
      aside={
        loaded.state === 'ready' && loaded.value && loaded.value.pending > 0 ? (
          // Batch 2 review, finding 5 — a pending count, not money that can
          // still move; the DESIGN §4 tone table lists "pending" under
          // neutral by name.
          <Chip tone="neutral">{t('ops.changes.awaiting', { count: loaded.value.pending })}</Chip>
        ) : null
      }
    >
      {!shown ? (
        <div className="form-actions">
          <button className="primary" onClick={onShow}>{t('ops.changes.show')}</button>
        </div>
      ) : (
        <>
          {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
          {loaded.state === 'loading' ? <Loading rows={2} /> : null}
          {loaded.state === 'ready' && loaded.value ? (
            loaded.value.items.length === 0 ? (
              <Empty>{t('ops.changes.none')}</Empty>
            ) : (
              <Sorted rows={loaded.value.items} by={{ summary: (r) => sortableCell(r.summary), reason: (r) => sortableCell(r.reason), status: (r) => sortableCell(r.status), decided_by_email: (r) => sortableCell(r.decided_by_email), requested_at: (r) => sortableCell(r.requested_at) }}>
                {(ordered, sort) => (
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <SortTh sort={sort} col="summary">{t('ops.changes.what')}</SortTh><SortTh sort={sort} col="reason">{t('ops.changes.why')}</SortTh><SortTh sort={sort} col="status">{t('common.status')}</SortTh><SortTh sort={sort} col="decided_by_email">{t('ops.changes.decidedBy')}</SortTh>
                      <SortTh sort={sort} col="requested_at" align="num">{t('ops.changes.proposed')}</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((r) => (
                      <tr key={r.id}>
                        <td>{r.summary}</td>
                        <td className="faint">{r.reason}</td>
                        <td>
                          <Chip
                            tone={
                              r.status === 'APPROVED'
                                ? 'locked'
                                : r.status === 'PENDING'
                                  ? 'provisional'
                                  : 'overdue'
                            }
                          >
                            {r.status}
                          </Chip>
                        </td>
                        <td className="faint">
                          {r.decided_by_email ?? '—'}
                          {r.decision_reason ? ` — ${r.decision_reason}` : ''}
                        </td>
                        <td className="num faint">{formatDate(r.requested_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
                )}
              </Sorted>
            )
          ) : null}
          <Callout>
            {t('ops.changes.note')}
          </Callout>
        </>
      )}
    </Panel>
  );
}
